import { Injectable } from '@angular/core';
import { forkJoin, Observable, of} from 'rxjs';
import { HttpParams } from '@angular/common/http';
import {catchError, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import { AuthenticationService } from '../core/authentication.service';
import { IGeography } from './geography';
import { IAddressValidation } from '../shared/components/address/address.service';
import { IUser } from './users';
import { IResponse } from '../batches/batches.service';
import { isNil } from 'lodash-es';
import { IPolicy } from '../core/password-policy';
import { MIHttpClientService } from '../shared/mi-http-client.service';
import { IdName } from '../shared/common-interfaces';
import { CountryStatesService } from '../shared/country-states-service';
import {IFormVisit} from '../visits/builder/formDefinition';
import {IVisit} from '../visits/visits/visit';
import { QueueService } from '../viba/admin/queues/queue.service';
import {RolesService} from "./roles/roles.service";


@Injectable({
  providedIn: 'root',
})
export class UsersService {

  PASSWORD_CHANGE_REQUEST_STATUS_SUCCESS = 1;
  PASSWORD_CHANGE_REQUEST_STATUS_USERNAME_PASSWORD_MISMATCH = -1;
  PASSWORD_CHANGE_REQUEST_STATUS_NEW_AND_RECENT_PASSWORDS_SAME = -2;
  PASSWORD_CHANGE_REQUEST_STATUS_TOO_MANY_PASSWORD_CHANGES = -3;
  PASSWORD_FAILS_PASSWORD_POLICY = -4;

  constructor(
    private miClient: MIHttpClientService,
    private countryStatesService: CountryStatesService,
    public authenticationService: AuthenticationService,
    private queueService: QueueService,
    private rolesService: RolesService,
  ) {
    this.authenticationService.selectedOrganization = +localStorage.getItem('selectedOrganization');
  }

  getStatesForCountry(countryId: number): Observable<any> {
    return this.miClient.get('states/byCountry/' + countryId);
  }

  validateAddress(address: IAddressValidation) {
    return this.miClient.postNoOrg('address/validate', address).pipe(
      tap(response => {
          response.validAddress = response.validAddress && this.unchangedFields(address, response, ['countryId', 'stateId', 'city', 'zip']);
        },
      ),
    );
  }

  unchangedFields(initialAddress, validatedAddress, fields: string[]): boolean {
    fields.forEach(field => {
      if (initialAddress[field] !== validatedAddress[field]) {
        return false;
      }
    });
    return true;
  }

  getReportingUsers(roleId: number, excludeUserId: number): Observable<IUser[]> {
    return this.miClient.get<IUser[]>('users/reportingUsers/' + roleId).pipe(
      map(users => {
        return users.filter(user =>  isNil(excludeUserId) || user.userId !== excludeUserId);
      })
    );
  }

  getClusters(division): Observable<any> {
    if (isNil(division) || '' === division || 'All' === division) {
      return of([]);
    }
    return this.miClient.get<any>('clustersGivenDivision?division=' + encodeURIComponent(division));
  }

  getDivisions(): Observable<string[]> {
    return this.miClient.get<any>('divisions');
  }

  getRegions(): Observable<IGeography[]> {
    return this.miClient.get<any>('geographies');
  }

  getFilteredRegions(division, cluster): Observable<IGeography[]> {
    return this.getRegions().pipe(map(regions => regions.filter(obj => obj.division === division && obj.market === cluster && obj.region !== null)));
  }

  getFilteredRegionsAsIdName(division, cluster): Observable<IdName[]> {
    return this.getFilteredRegions(division, cluster).pipe(map(regions => {
      const regionsIdName = [];
      regions.forEach(obj => {
          regionsIdName.push({
            id: obj.id,
            name: obj.region,
          });
      });
      return regionsIdName;
    }));
  }



  getUsersListData(): Observable<any> {
    const roles = this.rolesService.getRoles();
    const divisions = this.miClient.get('divisions');

    return forkJoin([roles, divisions]);
  }

  deleteUser(id: number) {
    return this.miClient.delete(`users/deleteUser/${id}`);
  }

  unDeleteUser(id: any) {
    return this.miClient.get('users/undeleteUser/' + id);
  }

  createUser(user: any): Observable<any> {
    return this.miClient.post('users', user);
  }

  getUser(id: number): Observable<any> {
    return this.miClient.get<IUser>('users/' + `${id}`);
  }

  getUsers(userIdList: string): Observable<any> {

    if (isNil(userIdList)) {
      return of(null);
    }

    const parts = userIdList.split(',');

    const observableBatch = [];
    parts.forEach((item) => {
      observableBatch.push(this.getUser(+item));
    });
    return forkJoin(observableBatch);
  }


  getUpdateUserData(id: number): Observable<any> {
    const user$ = this.miClient.get<IUser>('users/' + id);
    const roles$ = this.rolesService.getNonMIDBARoles();
    const countries$ = this.countryStatesService.getCountries();
    const divisions$ = this.getDivisions();
    const queues$ = this.queueService.getUserQueues(id);

    return forkJoin([user$, roles$, countries$, divisions$, queues$]).pipe(switchMap(([user, roles, countries, divisions, queues]) => {
      const roleId = this.rolesService.getRoleIdFromName(user.bossRole, roles);
      return forkJoin([
        of(user),
        of(roles),
        of(countries),
        of(divisions),
        this.getClusters(user.division),
        this.getFilteredRegions(user.division, user.cluster),
        this.rolesService.getReportingRoles(user.roleId),
        this.getReportingUsers(roleId, user?.userId),
        of(queues),
      ]);
    }));
  }

  getCreateUserData(): Observable<any> {

    const roles = this.rolesService.getNonMIDBARoles();
    const divisions = this.miClient.get('divisions');
    const countries = this.countryStatesService.getCountries();

    return forkJoin([roles, divisions, countries]);
  }

  public updateUser(user: any, userId: number): Observable<any> {
    return this.miClient.post('users/' + userId, user);
  }

  filterUsersByUserName(name): Observable<IResponse> {
    if (name === null || typeof name !== 'string') {
      return of({ list: [], TotalRowCount: 0 });
    }
    return this.miClient.get<IResponse>('users?showOnlyHierarchyUsers=true&userName=' + encodeURIComponent(name.trim()));
  }

  usernameExists(name): Observable<boolean> {
    return this.miClient.get<boolean>('users/usernameExists/' + encodeURIComponent(name.trim()));
  }


  getHierarchyUsers(): Observable<any> {
    return this.miClient.get<IResponse>(`users?showOnlyHierarchyUsers=true`);
  }

  public resetPassword(data: any): Observable<any> {
    let url = 'users/resetPassword/' + data.userId + '?sendToAdmin=' + data.sendToAdmin;
    if (!isNil(data.email) && data.email.length > 0) {
      url += '&emailAddress=' + encodeURIComponent(data.email);
    }
    return this.miClient.get(url);
  }

  saveUserAttributesForm(visit: IVisit): Observable<IFormVisit> {
    return this.miClient.post('users/userAttributes', visit, null, true, false);
  }

  getOrgUsers(maxUsersToReturn: number = 100): Observable<IUser[]> {
    const options = { params: new HttpParams({ fromObject: { pageSize: `${maxUsersToReturn}` } }) };
    return this.filterUsers(options);
  }

  filterUsers(options): Observable<IUser[]> {
    if (!Object.keys(options).includes('orderBy')) {
      options['orderBy'] = `LastName`;
    }
    return this.miClient.get<any>('users', { params: options })
      .pipe(
        map((users) => users['list']),
      );
  }

  resetPasswordSelf(username: string, emailAddress: string): Observable<any> {
    return this.miClient.get<any>('users/passwordReset',
      { params: new HttpParams({ fromObject: { username, emailAddress } }) }, false);
  }

  getPolicy(): Observable<IPolicy> {
    return this.miClient.get<IPolicy>('passwordPolicy');
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.miClient.postNoOrg('users/passwordChange', { currentPassword, newPassword }, false).pipe(
      catchError(response => of(response.error)),
    );
  }

  downlineUsersExist(): Observable<boolean> {
    return this.miClient.get<boolean>('users/downlineUsersExist');
  }
}
